import { TreeKeyManager, TreeKeyManagerStrategy } from '@angular/cdk/a11y';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  untracked,
  viewChildren,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReplaySubject } from 'rxjs';

import {
  CompanyNode,
  CompanyTreeRow,
} from 'src/app/app-common/types/company-tree';
import { CompanyTreeService } from 'src/app/app-common/services/company-tree.service';
import { CompanyTreeRowComponent } from 'src/app/app-common/components/company-tree-row/company-tree-row.component';
import { LoadMoreSentinelDirective } from 'src/app/components/scrolling-list/load-more-sentinel.directive';

@Component({
  selector: 'company-tree',
  templateUrl: './company-tree.component.html',
  styleUrl: './company-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CompanyTreeRowComponent,
    LoadMoreSentinelDirective,
    MatProgressSpinnerModule,
  ],
  host: {
    class: 'company-tree',
    role: 'tree',
    '[attr.aria-label]': '"Companies"',
    '(keydown)': 'onKeydown($event)',
  },
})
export class CompanyTreeComponent {
  private readonly treeService = inject(CompanyTreeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rowComponents = viewChildren(CompanyTreeRowComponent);
  private readonly items$ = new ReplaySubject<CompanyTreeRowComponent[]>(1);

  private keyManager: TreeKeyManagerStrategy<CompanyTreeRowComponent> | null =
    null;

  /**
   * Scroll container that clips the tree, used as the IntersectionObserver
   * `root` for the paging sentinels. The tree host itself grows with its
   * content, so it can never be the root: every sentinel would sit inside it
   * and page forever without the user scrolling.
   */
  readonly scrollRoot = input<Element | null>(null);
  /**
   * Non-null makes the component the search results surface, projecting these
   * matches instead of the tree's own rows. The tree still knows nothing
   * about search: it takes the matches as data.
   */
  readonly searchResults = input<readonly CompanyNode[] | null>(null);
  readonly selectCompany = output<string>();

  protected readonly rows = computed(() => {
    const results = this.searchResults();
    return results
      ? this.treeService.projectSearchRows(results)
      : this.treeService.rows();
  });

  protected readonly sentinelAfterIndex = computed(() => {
    const rows = this.rows();
    const sentinels = new Map<number, string[]>();

    rows.forEach((row, index) => {
      if (!row.hasMore) {
        return;
      }

      let last = index;
      for (let i = index + 1; i < rows.length; i++) {
        if (rows[i].level <= row.level) {
          break;
        }
        last = i;
      }

      const parentIds = sentinels.get(last) ?? [];
      parentIds.push(row.id);
      sentinels.set(last, parentIds);
    });

    return sentinels;
  });

  /**
   * Row indexes that end a branch currently fetching a further page. One entry
   * per index rather than per branch, so two branches ending on the same row
   * cannot stack two spinners.
   */
  private readonly loadingMoreAfterIndex = computed(() => {
    const loadingIds = new Set(
      this.rows()
        .filter((row) => row.loadingMore)
        .map((row) => row.id),
    );
    const indexes = new Set<number>();
    if (loadingIds.size === 0) {
      return indexes;
    }

    this.sentinelAfterIndex().forEach((parentIds, index) => {
      if (parentIds.some((parentId) => loadingIds.has(parentId))) {
        indexes.add(index);
      }
    });

    return indexes;
  });

  constructor() {
    afterNextRender(() => {
      this.keyManager = new TreeKeyManager(this.items$, {
        typeAheadDebounceInterval: true,
        trackBy: (row) => row.row().key,
      });
      this.destroyRef.onDestroy(() => this.keyManager?.destroy());

      const changeSub = this.keyManager.change.subscribe((item) => {
        if (item) {
          this.maybeLoadMoreFor(item.row());
        }
      });
      this.destroyRef.onDestroy(() => changeSub.unsubscribe());

      this.syncRows();
    });

    effect(() => {
      this.rowComponents();
      untracked(() => this.syncRows());
    });
  }

  protected onKeydown(event: KeyboardEvent): void {
    // The row itself opens the new tab on a modified Enter (Ctrl/Cmd+Enter);
    // this guard only stops that same keypress from also reaching
    // TreeKeyManager, which does not check modifiers and would otherwise
    // select the row too. A modified Space is left alone for the same reason.
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
    ) {
      return;
    }

    this.keyManager?.onKeydown(event);
  }

  protected onSelect(id: string): void {
    const results = this.searchResults();
    if (results) {
      void this.treeService.select(
        id,
        'search',
        results.find((result) => result.id === id),
      );
    } else {
      void this.treeService.select(id, 'tree');
    }
    this.selectCompany.emit(id);
  }

  protected onToggle(id: string): void {
    if (this.searchResults()) {
      void this.treeService.toggleSearchNode(id);
    } else {
      void this.treeService.toggle(id);
    }
  }

  protected onRetry(id: string): void {
    void this.treeService.retry(id);
  }

  protected onReveal(id: string): void {
    void this.treeService.revealChildren(id);
  }

  protected onHide(id: string): void {
    this.treeService.hideChildren(id);
  }

  protected onLoadMore(id: string): void {
    void this.treeService.loadMore(id);
  }

  protected sentinelParentIds(index: number): string[] {
    return this.sentinelAfterIndex().get(index) ?? [];
  }

  protected showLoadingMoreAfter(index: number): boolean {
    return this.loadingMoreAfterIndex().has(index);
  }

  private syncRows(): void {
    if (!this.keyManager) {
      return;
    }

    const items = [...this.rowComponents()];
    this.linkRows(items);

    // Publishing the items makes TreeKeyManager activate the first row, so the
    // previous active item has to be captured before that happens.
    const previous = this.keyManager.getActiveItem();
    const stillPresent = previous ? items.includes(previous) : false;

    this.items$.next(items);

    if (!stillPresent && items.length > 0) {
      const target = items.find((item) => item.row().selected) ?? items[0];
      this.keyManager.focusItem(target, { emitChangeEvent: false });
    }
  }

  private linkRows(items: readonly CompanyTreeRowComponent[]): void {
    items.forEach((item, index) => {
      const level = item.row().level;
      let parent: CompanyTreeRowComponent | null = null;
      for (let i = index - 1; i >= 0; i--) {
        if (items[i].row().level === level - 1) {
          parent = items[i];
          break;
        }
      }

      const children: CompanyTreeRowComponent[] = [];
      for (let i = index + 1; i < items.length; i++) {
        const childLevel = items[i].row().level;
        if (childLevel <= level) {
          break;
        }
        if (childLevel === level + 1) {
          children.push(items[i]);
        }
      }

      item.setTreeLinks(parent, children);
    });
  }

  private maybeLoadMoreFor(row: CompanyTreeRow): void {
    const rows = this.rows();
    const index = rows.findIndex((item) => item.key === row.key);
    if (index < 0) {
      return;
    }

    for (const parentId of this.sentinelParentIds(index)) {
      if (parentId !== row.id) {
        this.onLoadMore(parentId);
      }
    }
  }
}
